import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Share2, Link2, Copy, X, Loader2, Lightbulb, Navigation, Download } from "lucide-react";
import { api, pdfUrl, csvUrl, iftaPdfUrl, downloadPdf, BACKEND_BASE } from "@/lib/api";
import { CATEGORIES, CATEGORY_COLORS, fmtMoney } from "@/lib/theme";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const year = new Date().getFullYear();

export default function Taxes() {
  const [summary, setSummary] = useState(null);
  const [ifta, setIfta] = useState(null);
  const [mileage, setMileage] = useState(null);
  const [shares, setShares] = useState([]);
  const [quarter, setQuarter] = useState(Math.floor(new Date().getMonth() / 3) + 1);
  const [exporting, setExporting] = useState(false);
  const [csvBusy, setCsvBusy] = useState(false);
  const [iftaBusy, setIftaBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revokeToken, setRevokeToken] = useState(null);

  const loadIfta = useCallback(async (q) => {
    try {
      const d = await api(`/reports/ifta?year=${year}&quarter=${q}`);
      setIfta(d);
    } catch (e) {
      console.error("Failed to load IFTA report:", e);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const [sum, mi, sh] = await Promise.all([
        api(`/reports/tax-summary?year=${year}`),
        api(`/mileage/summary?year=${year}`),
        api("/shares"),
      ]);
      setSummary(sum);
      setMileage(mi);
      setShares(sh);
    } catch {
      setSummary({ ytd_total: 0, receipt_count: 0, by_category: [] });
    }
  }, []);

  useEffect(() => {
    document.title = "Taxes & IFTA · RigBooks";
    load();
  }, [load]);

  useEffect(() => {
    loadIfta(quarter);
  }, [quarter, loadIfta]);

  const byCat = CATEGORIES.map((c) => {
    const row = summary?.by_category?.find((x) => x.category === c);
    return { category: c, total: row?.total || 0, count: row?.count || 0 };
  });
  const ytdTotal = summary?.ytd_total || 0;

  const exportYear = async () => {
    setExporting(true);
    try {
      await downloadPdf(pdfUrl(`date_from=${year}-01-01&date_to=${year}-12-31`), `rigbooks_${year}.pdf`);
    } catch (e) {
      toast.error(e?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const exportCsv = async () => {
    setCsvBusy(true);
    try {
      await downloadPdf(csvUrl(`date_from=${year}-01-01&date_to=${year}-12-31`), `rigbooks_${year}.csv`);
    } catch (e) {
      toast.error(e?.message || "Export failed");
    } finally {
      setCsvBusy(false);
    }
  };

  const exportIftaPdf = async () => {
    setIftaBusy(true);
    try {
      await downloadPdf(iftaPdfUrl(`year=${year}&quarter=${quarter}`), `rigbooks_ifta_${year}_Q${quarter}.pdf`);
    } catch (e) {
      toast.error(e?.message || "Export failed");
    } finally {
      setIftaBusy(false);
    }
  };

  const shareLink = (token) => `${window.location.origin}/share/${token}`;

  const createShare = async () => {
    setCreating(true);
    try {
      const s = await api("/shares", {
        method: "POST",
        body: { label: `${year} CPA share`, year, expires_in_days: 60 },
      });
      await load();
      try {
        await navigator.clipboard.writeText(shareLink(s.token));
        toast.success("Read-only CPA link created & copied to clipboard.");
      } catch {
        toast.success("Read-only CPA link created.");
      }
    } catch (e) {
      toast.error(e?.message || "Couldn't create share.");
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (token) => {
    try {
      await navigator.clipboard.writeText(shareLink(token));
      toast.success("Link copied to clipboard.");
    } catch {
      toast.error("Couldn't copy. Link: " + shareLink(token));
    }
  };

  const revoke = async () => {
    const token = revokeToken;
    setRevokeToken(null);
    try {
      await api(`/shares/${token}`, { method: "DELETE" });
      toast.success("Share link revoked.");
      load();
    } catch (e) {
      toast.error(e?.message || "Revoke failed.");
    }
  };

  if (!summary) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 bg-rig-surface" />
        <Skeleton className="h-64 bg-rig-surface" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* YTD hero + mileage */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-rig-accent/60 bg-rig-surface p-6 lg:col-span-2">
          <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-rig-accent">
            {year} Year-to-Date Deductibles
          </p>
          <div className="mt-2 font-display text-5xl font-bold text-white" data-testid="ytd-total">{fmtMoney(ytdTotal)}</div>
          <p className="mt-1 text-sm text-rig-dim">{summary.receipt_count} receipts · audit-ready</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Button
              onClick={exportYear}
              disabled={exporting}
              data-testid="export-year-pdf"
              className="h-12 bg-rig-accent font-display text-sm font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark"
            >
              {exporting ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <FileText className="mr-2 h-5 w-5" />}
              Export {year} PDF
            </Button>
            <Button
              onClick={exportCsv}
              disabled={csvBusy}
              variant="outline"
              data-testid="export-year-csv"
              className="h-12 border-rig-borderStrong bg-transparent font-display text-sm font-bold uppercase tracking-widest text-white hover:bg-rig-bg hover:text-rig-accent"
            >
              {csvBusy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Download className="mr-2 h-5 w-5" />}
              CSV
            </Button>
          </div>
        </div>
        <div className="rounded-2xl border border-rig-border bg-rig-surface p-6">
          <div className="flex items-center gap-2">
            <Navigation className="h-4 w-4 text-rig-blue" />
            <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-rig-dim">Mileage Deduction</p>
          </div>
          <div className="mt-2 font-display text-4xl font-bold text-white">
            {fmtMoney(mileage?.deduction_usd || 0)}
          </div>
          <p className="mt-1 text-sm text-rig-dim">
            {(mileage?.total_miles || 0).toLocaleString()} mi @ ${mileage?.rate_per_mile ?? 0.67}/mi
          </p>
        </div>
      </div>

      {/* By category */}
      <div>
        <h3 className="mb-3 font-display text-sm font-bold uppercase tracking-[0.18em] text-rig-dim">By Category</h3>
        <div className="grid gap-2 sm:grid-cols-2">
          {byCat.map((c) => (
            <div key={c.category} className="flex items-center gap-3 rounded-xl border border-rig-border bg-rig-surface px-4 py-3">
              <span className="h-8 w-1.5 rounded-full" style={{ background: CATEGORY_COLORS[c.category] }} />
              <div className="flex-1">
                <div className="font-semibold text-white">{c.category}</div>
                <div className="text-xs text-rig-dim">{c.count} receipts</div>
              </div>
              <div className="font-display text-xl font-bold text-white">{fmtMoney(c.total)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* IFTA */}
      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-display text-sm font-bold uppercase tracking-[0.18em] text-rig-dim">
            IFTA Quarterly · {year}
          </h3>
          <div className="flex gap-2">
            {[1, 2, 3, 4].map((q) => (
              <button
                key={q}
                onClick={() => setQuarter(q)}
                data-testid={`ifta-q${q}`}
                className={`rounded-full border px-4 py-1.5 font-display text-sm font-bold tracking-widest ${
                  quarter === q
                    ? "border-rig-accent bg-rig-accent text-rig-accentText"
                    : "border-rig-border bg-rig-surface text-rig-dim hover:text-white"
                }`}
              >
                Q{q}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-rig-border bg-rig-surface p-5" data-testid="ifta-card">
          <div className="grid grid-cols-3 gap-4 border-b border-rig-border pb-4">
            <div>
              <div className="font-display text-[11px] uppercase tracking-widest text-rig-dim">Total Miles</div>
              <div className="mt-1 font-display text-2xl font-bold text-white">
                {(ifta?.total_miles || 0).toLocaleString()}
              </div>
            </div>
            <div>
              <div className="font-display text-[11px] uppercase tracking-widest text-rig-dim">Gallons</div>
              <div className="mt-1 font-display text-2xl font-bold text-white">
                {(ifta?.total_gallons || 0).toFixed(1)}
              </div>
            </div>
            <div>
              <div className="font-display text-[11px] uppercase tracking-widest text-rig-dim">Fleet MPG</div>
              <div className="mt-1 font-display text-2xl font-bold" style={{ color: ifta?.fleet_mpg > 0 ? "#00E676" : "#94A3B8" }}>
                {ifta?.fleet_mpg > 0 ? ifta.fleet_mpg.toFixed(1) : "—"}
              </div>
            </div>
          </div>

          {!ifta || ifta.rows.length === 0 ? (
            <p className="py-6 text-center text-sm text-rig-dim">
              Log trips &amp; fuel receipts (with state + gallons) to build your IFTA report for Q{quarter}.
            </p>
          ) : (
            <table className="mt-4 w-full text-left">
              <thead>
                <tr className="font-display text-[11px] uppercase tracking-widest text-rig-dim">
                  <th className="pb-2">State</th>
                  <th className="pb-2 text-right">Miles</th>
                  <th className="pb-2 text-right">Gallons</th>
                  <th className="pb-2 text-right">Net Gal</th>
                </tr>
              </thead>
              <tbody>
                {ifta.rows.map((row) => (
                  <tr key={row.state} className="border-t border-rig-border">
                    <td className="py-2.5 font-semibold text-white">{row.state}</td>
                    <td className="py-2.5 text-right text-white">{row.miles.toLocaleString()}</td>
                    <td className="py-2.5 text-right text-white">{row.purchased_gallons.toFixed(1)}</td>
                    <td
                      className="py-2.5 text-right font-bold"
                      style={{ color: row.net_gallons > 0 ? "#F59E0B" : "#10B981" }}
                    >
                      {row.net_gallons.toFixed(1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Button
            onClick={exportIftaPdf}
            disabled={iftaBusy}
            variant="outline"
            data-testid="export-ifta-pdf"
            className="mt-4 h-11 w-full border-rig-borderStrong bg-transparent font-display text-xs font-bold uppercase tracking-widest text-white hover:bg-rig-bg hover:text-rig-accent"
          >
            {iftaBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
            Export Q{quarter} IFTA PDF
          </Button>
        </div>
      </div>

      {/* Bookkeeper share */}
      <div>
        <h3 className="mb-3 font-display text-sm font-bold uppercase tracking-[0.18em] text-rig-dim">Bookkeeper / CPA</h3>
        <Button
          onClick={createShare}
          disabled={creating}
          data-testid="create-bookkeeper-share"
          className="h-12 w-full border border-rig-accent bg-transparent font-display text-sm font-bold uppercase tracking-widest text-rig-accent hover:bg-rig-accent/10"
        >
          {creating ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Share2 className="mr-2 h-5 w-5" />}
          Generate Read-Only Link
        </Button>

        <div className="mt-3 space-y-2">
          {shares.map((s) => (
            <div
              key={s.token}
              data-testid={`share-${s.token.slice(0, 8)}`}
              className="flex items-center gap-3 rounded-xl border border-rig-border bg-rig-surface px-4 py-3"
            >
              <Link2 className="h-4 w-4 shrink-0 text-rig-accent" />
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold text-white">{s.label}</div>
                <div className="text-xs text-rig-dim">Expires {String(s.expires_at).slice(0, 10)}</div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => copyLink(s.token)} data-testid={`copy-share-${s.token.slice(0, 8)}`} className="text-rig-accent hover:bg-rig-surface2">
                <Copy className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setRevokeToken(s.token)} className="text-rig-error hover:bg-rig-surface2">
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-xl border border-rig-border bg-rig-surface p-4">
        <Lightbulb className="h-5 w-5 shrink-0 text-rig-warning" />
        <p className="text-sm text-rig-dim">
          IRS lets you deduct <b className="text-white">$80/day</b> for meals on the road (DOT per-diem). Track those
          food receipts — they add up fast.
        </p>
      </div>

      <ConfirmDialog
        open={!!revokeToken}
        onOpenChange={(o) => !o && setRevokeToken(null)}
        title="Revoke this link?"
        description="Your CPA will no longer be able to view this report."
        confirmLabel="Revoke"
        destructive
        onConfirm={revoke}
        testID="revoke-share-confirm"
      />
    </div>
  );
}
